/**
 * Página de Lançamentos
 * Gerencia despesas e faturamento
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { listarDespesas, criarDespesa, atualizarDespesa, deletarDespesa } from '../../services/despesasService';
import { listarFaturamento, criarFaturamento, atualizarFaturamento, deletarFaturamento } from '../../services/faturamentoService';

/**
 * TabPanel para alternar entre abas
 */
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Página de Lançamentos
 */
const Lancamentos = () => {
  const [tabValue, setTabValue] = useState(0);
  const [despesas, setDespesas] = useState([]);
  const [faturamentos, setFaturamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalTipo, setModalTipo] = useState('despesa'); // 'despesa' ou 'faturamento'
  const [modoEdicao, setModoEdicao] = useState(false);
  const [itemEdicao, setItemEdicao] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    categoria: '',
    tipo: 'variavel',
    observacoes: '',
    // Faturamento
    paciente: '',
    formaPagamento: '',
    tipoPessoa: 'PF'
  });

  const categoriasDespesas = [
    'Aluguel', 'Equipamentos', 'Materiais', 'Salários', 
    'Impostos', 'Marketing', 'Manutenção', 'Outros'
  ];

  const formasPagamento = [
    'Dinheiro', 'Cartão de Crédito', 'Cartão de Débito', 
    'PIX', 'Transferência', 'Cheque', 'Outros'
  ];

  // Carrega dados ao montar
  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [despesasData, faturamentosData] = await Promise.all([
        listarDespesas(),
        listarFaturamento()
      ]);
      console.log('📊 Despesas:', despesasData);
      console.log('💰 Faturamentos:', faturamentosData);
      setDespesas(despesasData.data || []);
      setFaturamentos(faturamentosData.data || []);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar lançamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const abrirModalNovo = (tipo) => {
    setModalTipo(tipo);
    setModoEdicao(false);
    setItemEdicao(null);
    setFormData({
      descricao: '',
      valor: '',
      data: new Date().toISOString().split('T')[0],
      categoria: '',
      tipo: 'variavel',
      observacoes: '',
      paciente: '',
      formaPagamento: '',
      tipoPessoa: 'PF'
    });
    setOpenModal(true);
  };

  const abrirModalEdicao = (item, tipo) => {
    setModalTipo(tipo);
    setModoEdicao(true);
    setItemEdicao(item);
    setFormData({
      descricao: item.descricao || '',
      valor: item.valor || '',
      data: item.data || '',
      categoria: item.categoria || '',
      tipo: item.tipo || 'variavel',
      observacoes: item.observacoes || '',
      paciente: item.paciente || '',
      formaPagamento: item.formaPagamento || '',
      tipoPessoa: item.tipoPessoa || 'PF'
    });
    setOpenModal(true);
  };

  const fecharModal = () => {
    setOpenModal(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setLoading(true);

      if (modalTipo === 'despesa') {
        const dados = {
          descricao: formData.descricao,
          valor: parseFloat(formData.valor),
          data: formData.data,
          categoria: formData.categoria,
          tipo: formData.tipo,
          observacoes: formData.observacoes
        };

        if (modoEdicao) {
          await atualizarDespesa(itemEdicao.id, dados);
          setSuccess('Despesa atualizada com sucesso!');
        } else {
          await criarDespesa(dados);
          setSuccess('Despesa criada com sucesso!');
        }
      } else {
        const dados = {
          descricao: formData.descricao,
          valor: parseFloat(formData.valor),
          data: formData.data,
          paciente: formData.paciente,
          formaPagamento: formData.formaPagamento,
          tipoPessoa: formData.tipoPessoa,
          observacoes: formData.observacoes
        };

        if (modoEdicao) {
          await atualizarFaturamento(itemEdicao.id, dados);
          setSuccess('Faturamento atualizado com sucesso!');
        } else {
          await criarFaturamento(dados);
          setSuccess('Faturamento criado com sucesso!');
        }
      }

      fecharModal();
      carregarDados();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setError(err.response?.data?.message || 'Erro ao salvar lançamento');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletar = async (id, tipo) => {
    if (!window.confirm('Tem certeza que deseja excluir?')) return;

    try {
      setLoading(true);
      if (tipo === 'despesa') {
        await deletarDespesa(id);
        setSuccess('Despesa excluída com sucesso!');
      } else {
        await deletarFaturamento(id);
        setSuccess('Faturamento excluído com sucesso!');
      }
      carregarDados();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Erro ao deletar:', err);
      setError('Erro ao excluir lançamento');
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data) => {
    return new Date(data + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const formatarMoeda = (valor) => {
    return parseFloat(valor).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  return (
    <Box>
      {/* Cabeçalho */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Lançamentos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => abrirModalNovo(tabValue === 0 ? 'despesa' : 'faturamento')}
        >
          Novo Lançamento
        </Button>
      </Box>

      {/* Alertas */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Tabs */}
      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Despesas (${despesas.length})`} />
          <Tab label={`Faturamentos (${faturamentos.length})`} />
        </Tabs>

        {/* Aba de Despesas */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : despesas.length === 0 ? (
            <Box textAlign="center" p={5}>
              <Typography color="textSecondary">
                Nenhuma despesa cadastrada ainda.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={() => abrirModalNovo('despesa')}
              >
                Adicionar primeira despesa
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {despesas.map((despesa) => (
                    <TableRow key={despesa.id}>
                      <TableCell>{formatarData(despesa.data)}</TableCell>
                      <TableCell>{despesa.descricao}</TableCell>
                      <TableCell>{despesa.categoria}</TableCell>
                      <TableCell>
                        <Chip 
                          label={despesa.tipo} 
                          size="small" 
                          color={despesa.tipo === 'fixa' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatarMoeda(despesa.valor)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => abrirModalEdicao(despesa, 'despesa')}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeletar(despesa.id, 'despesa')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Aba de Faturamento */}
        <TabPanel value={tabValue} index={1}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : faturamentos.length === 0 ? (
            <Box textAlign="center" p={5}>
              <Typography color="textSecondary">
                Nenhum faturamento cadastrado ainda.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={() => abrirModalNovo('faturamento')}
              >
                Adicionar primeiro faturamento
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Paciente</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Forma Pgto</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {faturamentos.map((faturamento) => (
                    <TableRow key={faturamento.id}>
                      <TableCell>{formatarData(faturamento.data)}</TableCell>
                      <TableCell>{faturamento.descricao}</TableCell>
                      <TableCell>{faturamento.paciente}</TableCell>
                      <TableCell>
                        <Chip 
                          label={faturamento.tipoPessoa} 
                          size="small" 
                          color={faturamento.tipoPessoa === 'PJ' ? 'secondary' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>{faturamento.formaPagamento}</TableCell>
                      <TableCell align="right">
                        {formatarMoeda(faturamento.valor)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => abrirModalEdicao(faturamento, 'faturamento')}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeletar(faturamento.id, 'faturamento')}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Modal de Criar/Editar */}
      <Dialog open={openModal} onClose={fecharModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {modoEdicao ? 'Editar' : 'Novo'} {modalTipo === 'despesa' ? 'Despesa' : 'Faturamento'}
            </Typography>
            <IconButton onClick={fecharModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Descrição"
                required
                fullWidth
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              />

              <TextField
                label="Valor"
                required
                fullWidth
                type="number"
                inputProps={{ step: '0.01', min: '0' }}
                value={formData.valor}
                onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
              />

              <TextField
                label="Data"
                required
                fullWidth
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.data}
                onChange={(e) => setFormData({ ...formData, data: e.target.value })}
              />

              {modalTipo === 'despesa' ? (
                <>
                  <FormControl fullWidth required>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      value={formData.categoria}
                      label="Categoria"
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    >
                      {categoriasDespesas.map((cat) => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={formData.tipo}
                      label="Tipo"
                      onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    >
                      <MenuItem value="fixa">Fixa</MenuItem>
                      <MenuItem value="variavel">Variável</MenuItem>
                    </Select>
                  </FormControl>
                </>
              ) : (
                <>
                  <TextField
                    label="Paciente"
                    required
                    fullWidth
                    value={formData.paciente}
                    onChange={(e) => setFormData({ ...formData, paciente: e.target.value })}
                  />

                  <FormControl fullWidth required>
                    <InputLabel>Forma de Pagamento</InputLabel>
                    <Select
                      value={formData.formaPagamento}
                      label="Forma de Pagamento"
                      onChange={(e) => setFormData({ ...formData, formaPagamento: e.target.value })}
                    >
                      {formasPagamento.map((forma) => (
                        <MenuItem key={forma} value={forma}>{forma}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth required>
                    <InputLabel>Tipo de Pessoa</InputLabel>
                    <Select
                      value={formData.tipoPessoa}
                      label="Tipo de Pessoa"
                      onChange={(e) => setFormData({ ...formData, tipoPessoa: e.target.value })}
                    >
                      <MenuItem value="PF">Pessoa Física</MenuItem>
                      <MenuItem value="PJ">Pessoa Jurídica</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}

              <TextField
                label="Observações"
                fullWidth
                multiline
                rows={3}
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={fecharModal}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Lancamentos;
